import { PaymentMode } from "../Invoice/Invoice";

export type VendorPaymentTiming = "advance" | "post_invoice";

/** Same stored values as the manual payment form. */
export const VENDOR_PAYMENT_CSV_PAYMENT_TIMING_VALUES: VendorPaymentTiming[] = [
  "advance",
  "post_invoice",
];

export const VENDOR_PAYMENT_CSV_PAYMENT_MODE_VALUES = Object.values(
  PaymentMode,
) as string[];

function firstNonEmptyName(
  ...values: (string | null | undefined)[]
): string | null {
  for (const v of values) {
    const s = typeof v === "string" ? v.trim() : "";
    if (s) return s;
  }
  return null;
}

/**
 * Maps list/create/update API payloads to VendorPaymentRow.
 * Backend may use `created_by_name`, `created_by_admin_name`, `created_by_adminname`, or camelCase.
 */
export function normalizeVendorPaymentRow(raw: unknown): VendorPaymentRow {
  const r = raw as Record<string, unknown> & VendorPaymentRow;
  return {
    ...r,
    created_by_name: firstNonEmptyName(
      r.created_by_name as string | null | undefined,
      r.created_by_admin_name as string | null | undefined,
      r.created_by_adminname as string | null | undefined,
      r.createdByName as string | null | undefined,
    ),
    updated_by_name: firstNonEmptyName(
      r.updated_by_name as string | null | undefined,
      r.updated_by_admin_name as string | null | undefined,
      r.updated_by_adminname as string | null | undefined,
      r.updatedByName as string | null | undefined,
    ),
    payment_date:
      (r.payment_date as string | null | undefined) ??
      (r.paymentDate as string | null | undefined) ??
      null,
    utr_no:
      firstNonEmptyName(
        r.utr_no as string | null | undefined,
        r.utr_number as string | null | undefined,
        r.utrNo as string | null | undefined,
      ) ?? null,
  };
}

export interface VendorPaymentRow {
  id: number;
  pr_id: number;
  amount: string | number;
  payment_timing: VendorPaymentTiming;
  created_at: Date;
  created_by: number | null;
  created_by_name: string | null;
  updated_at: Date;
  updated_by: number | null;
  updated_by_name: string | null;
  deleted_at: Date | null;
  deleted_by: number | null;
  deleted_by_name: string | null;
  active: number;
  payment_mode: string | null;
  remarks: string | null;
  payment_date: string | null;
  utr_no: string | null;
}

export interface VendorPaymentFormPayload {
  pr_id: number;
  amount: number;
  payment_timing: VendorPaymentTiming;
  payment_mode?: string | null;
  remarks?: string | null;
  payment_date?: string | null;
  utr_no?: string | null;
}

export interface VendorPaymentCsvUploadResult {
  created_count?: number;
  payments?: VendorPaymentRow[];
  errors?: VendorPaymentCsvUploadErrorItem[];
}

export type VendorPaymentCsvUploadErrorItem =
  | string
  | {
      row?: number;
      message?: string;
      error?: string;
    };

export function formatVendorPaymentCsvUploadErrors(errors: unknown): string[] {
  if (!errors) return [];
  if (!Array.isArray(errors)) {
    return [String(errors)];
  }
  return errors.map((entry) => {
    if (typeof entry === "string") return entry;
    const row = entry?.row;
    const text =
      entry?.message?.trim() || entry?.error?.trim() || JSON.stringify(entry);
    return row != null ? `Row ${row}: ${text}` : text;
  });
}

function linesFromMessage(message: string | undefined): string[] {
  if (!message?.trim()) return [];
  return message
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);
}

export function parseVendorPaymentCsvUploadError(error: unknown): string[] {
  const axiosErr = error as {
    response?: {
      data?: {
        message?: string;
        errors?: unknown;
        result?: { errors?: unknown } | unknown;
      };
    };
    message?: string;
  };
  const data = axiosErr.response?.data;
  const result = data?.result;
  const resultErrors =
    result && typeof result === "object" && !Array.isArray(result)
      ? (result as { errors?: unknown }).errors
      : result;
  const fromResult = formatVendorPaymentCsvUploadErrors(resultErrors);
  if (fromResult.length > 0) {
    return fromResult;
  }
  const fromDataErrors = formatVendorPaymentCsvUploadErrors(data?.errors);
  if (fromDataErrors.length > 0) {
    return fromDataErrors;
  }
  const fallbackLines = linesFromMessage(
    data?.message?.trim() || axiosErr.message?.trim(),
  );
  return fallbackLines.length > 0 ? fallbackLines : ["CSV upload failed."];
}
